import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Check, CreditCard, Loader2, Ruler } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { useBilling, startCheckout } from "@/components/Billing";
import { errMsg } from "@/lib/api";

const PERKS = [
  "No monthly fee - you only pay for the drafts you create",
  "Unlimited AI revisions within each draft's licensed size",
  "Multi-storey plans, lot & setback fitting, furniture & MEP layouts",
  "Rhino Live sync, .3dm, Rhino script and PDF drawing sets",
  "Design DNA and team seats included",
];

export default function Subscribe() {
  const { billing } = useBilling();
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const go = async () => {
    setBusy(true);
    try { await startCheckout("/billing/card/setup"); } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  useEffect(() => { document.title = "Add a card · RhinoDraft AI"; }, []);
  useEffect(() => {
    if (billing && !billing.active && params.get("start") === "1") go();
  }, [billing]); // eslint-disable-line react-hooks/exhaustive-deps
  if (billing?.active) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen blueprint-grid">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-start">
        <div className="rise">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--cyan)]">One step to start</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">Put a card on file</h1>
          <p className="mt-6 text-[var(--muted)] max-w-md leading-relaxed">RhinoDraft AI is pay-per-draft. Your card is saved securely with Stripe so each draft can be paid in one click. Nothing is charged now.</p>
          <div className="mt-8 flex items-center gap-3 border border-[var(--line)] bg-[var(--bg)]/70 px-4 py-3 max-w-md" data-testid="sqft-pricing-note">
            <Ruler size={18} className="text-[var(--amber)] shrink-0" />
            <p className="text-sm">Drafts are <span className="text-[var(--amber)] font-semibold">$1 per square foot</span> - e.g. a 1,500 sq ft home is $1,500, charged before the AI starts.</p>
          </div>
        </div>
        <div className="rise d2 border border-[var(--line)] bg-[var(--surface)] p-8" data-testid="card-setup-panel">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">Pay per draft</p>
          <div className="mt-3 flex items-end gap-2"><span className="text-5xl font-bold font-display" data-testid="per-sqft-price">$1</span><span className="text-[var(--muted)] mb-1.5">/ sq ft drafted</span></div>
          <ul className="mt-6 space-y-3">
            {PERKS.map((p) => <li key={p} className="flex gap-3 text-sm"><Check size={16} className="text-[var(--cyan)] shrink-0 mt-0.5" />{p}</li>)}
          </ul>
          <button data-testid="add-card-btn" onClick={go} disabled={busy || !billing}
            className="mt-8 w-full py-3.5 font-semibold bg-[var(--cyan)] text-[var(--bg)] hover:brightness-110 transition-[filter] disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Add card securely
          </button>
          <p className="mt-3 text-[11px] text-[var(--muted)] text-center">Secure by Stripe · no charge until you start a draft</p>
        </div>
      </main>
    </div>
  );
}
